import {
  Establishment,
  EstablishmentOnboarding,
  FeatureTrial,
  getEstablishmentFeaturesByPlan,
  getEstablishmentVerticals,
  getVerticalFeaturesByPlan,
  getActiveTrialFeatures,
  resolveFeatureDependencies,
  resolveEstablishmentCurrencyCode,
  SubscriptionInfo,
  firestoreDateParser,
  WorkingDays,
  Feature,
  hasProductCostsFeature,
  mergedTourIds,
  TourState,
  TourStates,
  tourStateFields,
} from '@pedix-workspace/utils';
import { FirestoreNestedConverter, withNestedConverter } from './utils';

export type EstablishmentConverterParams = {
  nestedConverter?: FirestoreNestedConverter<Establishment>;
};

export const DEFAULT_PRODUCT_EXPORTS: Establishment['productsExport'] = {
  enabled: false,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sources: [{ type: 'instagram', enabled: false } as any],
};

export const getEstablishmentConverter = ({ nestedConverter }: EstablishmentConverterParams = {}) =>
  withNestedConverter({
    fromFirestore(snapshot) {
      return convertEstablishmentData(snapshot.id, <Establishment>snapshot.data());
    },
    toFirestore(establishment: Establishment): Establishment {
      return establishment;
    },
  })(nestedConverter);

export const computeEstablishmentFeatures = (
  establishmentData: Pick<Partial<Establishment>, 'subscription' | 'featureTrials' | 'categories'>,
): { features: Feature[]; featureTrials: FeatureTrial[] } => {
  const plan = establishmentData.subscription?.plan || '';
  const planFeatures = [
    ...getEstablishmentFeaturesByPlan(plan),
    ...getVerticalFeaturesByPlan(plan, getEstablishmentVerticals(establishmentData.categories)),
  ];
  const featureTrials = getFeatureTrials(establishmentData.featureTrials, planFeatures);
  const activeTrialFeatures = getActiveTrialFeatures(featureTrials, 'all');
  const featureDependencies = resolveFeatureDependencies([...planFeatures, ...activeTrialFeatures]);

  return {
    features: [...new Set([...planFeatures, ...activeTrialFeatures, ...featureDependencies])],
    featureTrials,
  };
};

export const convertEstablishmentData = (
  establishmentId: string,
  establishmentData: Partial<Establishment>,
): Establishment => {
  const { features, featureTrials } = computeEstablishmentFeatures(establishmentData);
  const currencyCode = resolveEstablishmentCurrencyCode(establishmentData);

  return <Establishment>{
    ...establishmentData,
    id: establishmentId,
    features,
    featureTrials,
    currencyCode,
    userIds: establishmentData.userIds || {},
    contactInfo: establishmentData.contactInfo || {},
    links: establishmentData.links || {},
    addressDetails: establishmentData.addressDetails,
    productsExport: establishmentData.productsExport || DEFAULT_PRODUCT_EXPORTS,
    maxMultimediaRepositorySize: establishmentData.maxMultimediaRepositorySize || 5,
    isDigitalMenu: getIsDigitalMenu(features, establishmentData.isDigitalMenu),
    subscription: getSubscriptionInfo(establishmentData.subscription),
    workingDays: getWorkingDays(features, establishmentData.workingDays),
    temporaryClosure: getTemporaryClosure(features, establishmentData.temporaryClosure),
    activeInventoryCount: getActiveInventoryCount(features, establishmentData.activeInventoryCount),
    suppliesConfiguration: getSuppliesConfiguration(
      features,
      establishmentData.suppliesConfiguration,
    ),
    facebookPixel: getFacebookPixel(features, establishmentData.facebookPixel),
    googleAnalytics: getGoogleAnalytics(features, establishmentData.googleAnalytics),
    googleTagManager: getGoogleTagManager(features, establishmentData.googleTagManager),
    seoSocialMedia: getSeoSocialMedia(features, establishmentData.seoSocialMedia),
    theme: getTheme(features, establishmentData.theme),
    catalogConfiguration: getCatalogConfiguration(features, establishmentData.catalogConfiguration),
    announcements: getAnnouncements(features, establishmentData.announcements),
    security: getSecurity(features, establishmentData.security),
    onboarding: getOnboarding(establishmentData.onboarding),
  };
};

export const getOnboarding = (
  onboarding?: Establishment['onboarding'],
): Establishment['onboarding'] => {
  if (!onboarding) {
    return undefined;
  }
  return {
    ...onboarding,
    checklistCompletedAt: firestoreDateParser(onboarding.checklistCompletedAt),
    completedAt: firestoreDateParser(onboarding.completedAt),
    snoozedAt: firestoreDateParser(onboarding.snoozedAt),
    testOrderCompletedAt: firestoreDateParser(onboarding.testOrderCompletedAt),
    tourUsers: getTourUsers(onboarding.tourUsers),
  };
};

const getTourUsers = (
  tourUsers?: EstablishmentOnboarding['tourUsers'],
): EstablishmentOnboarding['tourUsers'] => {
  if (!tourUsers) {
    return undefined;
  }
  return Object.fromEntries(
    Object.entries(tourUsers).map(([uid, tours]) => [uid, getTourStates(tours ?? {})]),
  );
};

const getTourState = (state: TourState | undefined): TourState =>
  Object.fromEntries(tourStateFields.map(field => [field, firestoreDateParser(state?.[field])]));

const getTourStates = (stored: Record<string, TourState | undefined>): TourStates => {
  const states: Record<string, TourState> = Object.fromEntries(
    Object.entries(stored)
      .filter(([tourId]) => !(tourId in mergedTourIds))
      .map(([tourId, state]) => [tourId, getTourState(state)]),
  );
  for (const [mergedId, tourId] of Object.entries(mergedTourIds)) {
    const merged = stored[mergedId];
    if (!merged) {
      continue;
    }
    const current = states[tourId] ?? {};
    const legacy = getTourState(merged);
    states[tourId] = Object.fromEntries(
      tourStateFields.map(field => [field, current[field] ?? legacy[field]]),
    );
  }
  return states;
};

export const getSubscriptionInfo = (subscription?: SubscriptionInfo): SubscriptionInfo | null => {
  if (!subscription) {
    return null;
  }
  return {
    ...subscription,
    createdAt: firestoreDateParser(subscription.createdAt),
    updatedAt: firestoreDateParser(subscription.updatedAt),
    canceledAt: firestoreDateParser(subscription.canceledAt),
    currentPeriodStartDate: firestoreDateParser(subscription.currentPeriodStartDate),
    currentPeriodEndDate: firestoreDateParser(subscription.currentPeriodEndDate),
    trialPeriodStartDate: firestoreDateParser(subscription.trialPeriodStartDate),
    trialPeriodEndDate: firestoreDateParser(subscription.trialPeriodEndDate),
  };
};

export const getDefaultWorkingDays = (): WorkingDays => {
  return {
    isActive: false,
    enableOrdersOnClosed: false,
    selectedDaysOfWeek: [1, 2, 3, 4, 5, 6, 7],
    primaryWorkingHours: {},
    secondaryWorkingHours: {},
    customizeHoursByDay: false,
    daysOfWeekConfig: {
      1: { primaryWorkingHours: {}, secondaryWorkingHours: {} },
      2: { primaryWorkingHours: {}, secondaryWorkingHours: {} },
      3: { primaryWorkingHours: {}, secondaryWorkingHours: {} },
      4: { primaryWorkingHours: {}, secondaryWorkingHours: {} },
      5: { primaryWorkingHours: {}, secondaryWorkingHours: {} },
      6: { primaryWorkingHours: {}, secondaryWorkingHours: {} },
      7: { primaryWorkingHours: {}, secondaryWorkingHours: {} },
    },
  };
};

export const getWorkingDays = (
  features: Feature[],
  workingDays?: Establishment['workingDays'],
): Establishment['workingDays'] => {
  if (!features.includes('WORKING_HOURS') || !workingDays) {
    return getDefaultWorkingDays();
  }
  if (workingDays.enableOrdersOnClosed === undefined) {
    workingDays.enableOrdersOnClosed = true; // sets default value to true for backwards compatibility
  }
  return workingDays;
};

export const getTemporaryClosure = (
  features: Feature[],
  temporaryClosure?: Establishment['temporaryClosure'],
): Establishment['temporaryClosure'] => {
  if (!features.includes('WORKING_HOURS') || !temporaryClosure) {
    return undefined;
  }
  if (temporaryClosure.from) {
    temporaryClosure.from = firestoreDateParser(temporaryClosure.from) as Date;
  }
  if (temporaryClosure.to) {
    temporaryClosure.to = firestoreDateParser(temporaryClosure.to) as Date;
  }
  return temporaryClosure;
};

export const getActiveInventoryCount = (
  features: Feature[],
  activeInventoryCount?: Establishment['activeInventoryCount'],
): Establishment['activeInventoryCount'] => {
  if (!features.includes('INVENTORY_MANAGEMENT') || !activeInventoryCount) {
    return undefined;
  }
  return {
    ...activeInventoryCount,
    startedAt: firestoreDateParser(activeInventoryCount.startedAt) as Date,
  };
};

export const getSuppliesConfiguration = (
  features: Feature[],
  suppliesConfiguration?: Establishment['suppliesConfiguration'],
): Establishment['suppliesConfiguration'] => {
  if (!hasProductCostsFeature(features)) {
    return undefined;
  }
  return suppliesConfiguration;
};

export const getFacebookPixel = (
  features: Feature[],
  facebookPixel?: Establishment['facebookPixel'],
): Establishment['facebookPixel'] => {
  if (!features.includes('ANALYTICS')) {
    return undefined;
  }
  return facebookPixel;
};

export const getGoogleAnalytics = (
  features: Feature[],
  googleAnalytics?: Establishment['googleAnalytics'],
): Establishment['googleAnalytics'] => {
  if (!features.includes('ANALYTICS')) {
    return undefined;
  }
  return googleAnalytics;
};

export const getGoogleTagManager = (
  features: Feature[],
  googleTagManager?: Establishment['googleTagManager'],
): Establishment['googleTagManager'] => {
  if (!features.includes('ANALYTICS')) {
    return undefined;
  }
  return googleTagManager;
};

export const getSeoSocialMedia = (
  features: Feature[],
  seoSocialMedia?: Establishment['seoSocialMedia'],
): Establishment['seoSocialMedia'] => {
  if (!features.includes('SEO_SOCIAL_MEDIA') || !seoSocialMedia) {
    return undefined;
  }
  if (seoSocialMedia.displayShareButton === undefined) {
    seoSocialMedia.displayShareButton = true;
  }
  return seoSocialMedia;
};

export const getIsDigitalMenu = (
  features: Feature[],
  isDigitalMenu?: Establishment['isDigitalMenu'],
): Establishment['isDigitalMenu'] => {
  if (!features.includes('DIGITAL_MENU')) {
    return false;
  }
  return isDigitalMenu || false;
};

export const getTheme = (
  features: Feature[],
  theme?: Establishment['theme'],
): Establishment['theme'] => {
  if (!features.includes('THEME')) {
    return undefined;
  }
  return theme;
};

export const getCatalogConfiguration = (
  features: Feature[],
  catalogConfiguration: Establishment['catalogConfiguration'] = {
    minimumPurchase: 0,
    displaySku: false,
    skuInOrderMessage: false,
    stockDisplayCondition: 'less-than-equal',
    stockDisplayConditionValue: 7,
    displayProductPriceWithoutTaxes: false,
  },
): Establishment['catalogConfiguration'] => {
  if (catalogConfiguration.minimumPurchase === undefined) {
    catalogConfiguration.minimumPurchase = 0;
  }

  if (!features.includes('SKU') || catalogConfiguration.displaySku === undefined) {
    catalogConfiguration.displaySku = false;
  }
  if (catalogConfiguration.skuInOrderMessage === undefined) {
    catalogConfiguration.skuInOrderMessage = false;
  }
  if (catalogConfiguration.stockDisplayCondition === undefined) {
    catalogConfiguration.stockDisplayCondition = 'less-than-equal';
    catalogConfiguration.stockDisplayConditionValue = 7;
  }

  return catalogConfiguration;
};

export const getAnnouncements = (
  features: Feature[],
  announcements: Establishment['announcements'],
): Establishment['announcements'] => {
  if (!features.includes('ANNOUNCEMENTS')) {
    return undefined;
  }
  return announcements;
};

export const getFeatureTrials = (
  featureTrials: FeatureTrial[] = [],
  planFeatures: Feature[] = [],
): FeatureTrial[] => {
  return featureTrials
    .map(trial => ({
      ...trial,
      trialEndDate: trial.trialEndDate
        ? (firestoreDateParser(trial.trialEndDate) as Date)
        : undefined,
    }))
    .filter(trial => !planFeatures.includes(trial.feature));
};

export const getSecurity = (
  features: Feature[],
  security?: Establishment['security'],
): Establishment['security'] => {
  if (!features.includes('SECURITY') || !security) {
    return {
      mfaEnabled: false,
      mfaAdditionalContacts: [],
    };
  }
  return security;
};
